import { createContext, createRef, type FC, type ReactNode, useContext, useEffect, useRef, useState } from "react";
import * as UT from '../aio-utils';
import { $ } from 'aio-deps';
import './index.css';
export type AI_RangeContext = {
    rangeHook: ReturnType<typeof useRange>
}
const RangeContext = createContext<AI_RangeContext>({} as any);
const RangeProvider: FC<{ value: AI_RangeContext, children: ReactNode }> = ({ value, children }) => <RangeContext.Provider value={value}>{children}</RangeContext.Provider>
const useRangeProvider = () => useContext(RangeContext)
type AI_sbp = (size: number, half?: boolean) => number;
type AI_rbs = (range: I_range) => { thickness: number, color: string, roundCap: boolean, offset: number, className?: string, from: number, to: number }


export type AI_label = {
    list?: number[], start?: number, end?: number, step?: number, dynamic?: boolean, autoHide?: boolean, zIndex?: number,
    setting: (value: number, p: { angle: number, disabled: boolean, index: number }) => AI_labelItem
}
export type AI_labelItem = { offset?: number, fixAngle?: boolean, html?: ReactNode, config?: { thickness: number, height: number, color: string } }
export type I_range = { thickness: number, offset: number, color?: string, roundCap?: boolean, full?: boolean, className?: string, from: number, to: number, style?: any }
export type I_Range = {
    value?: any, start?: number, end?: number, max?: number, min?: number, step?: number, onChange?: (v: any) => void,
    multiple?: boolean, text?: ReactNode,
    style?: any, attrs?: any, className?: string, disabled?: boolean,
    reverse?: boolean, size?: number, vertical?: boolean, rotate?: number, round?: number,
    grooveAttrs?: { [key: string]: any },
    labels?: AI_label[],
    ranges?: I_range[],
    rangeOption?: {
        pointHtml?: (p: { index: number, value: number, disabled: boolean, angle: number }) => ReactNode | false | undefined,
        pointContent?: (p: { index: number, value: number, disabled: boolean, angle: number }) => ReactNode,
        pointOffset?: (p: { index: number, value: number, disabled: boolean, angle: number }) => number | undefined,
        disabled?: (value: number) => boolean,
        fill?: (startPoint: { index: number, value: number }, endPoint: { index: number, value: number }) => { thickness?: number, color?: string, className?: string, style?: any } | false | undefined,
        handle?: (p: { index: number, value: number, disabled: boolean, angle: number }) => { widthPercent?: number, heightPercent?: number, color?: string, offset?: number, sharp?: boolean } | undefined,
    },

}
type I_Range_default = I_Range & { start: number, end: number, step: number, min: number, max: number, size: number, labels: AI_label[], rotate: number, ranges: I_range[] }
export const AIOSlider: FC<I_Range> = (props) => {
    const rangeHook = useRange(props,'slider')
    return (
        <RangeProvider value={{ rangeHook }}>
            <div {...rangeHook.getRootProps(props, 'slider')}>
                <SliderGroove grooveAttrs={props.grooveAttrs} />
                <SliderRects ranges={props.ranges} />
                <SliderFills />
                <RangeLabels type='slider' />
                <RangeValues type={'slider'} />
            </div>
        </RangeProvider>
    )
}
export const AIOSpinner: FC<I_Range> = (props) => {
    const rangeHook = useRange(props,'spinner')
    return (
        <RangeProvider value={{ rangeHook }}>
            <div {...rangeHook.getRootProps(props, 'spinner')}>
                <SpinnerText text={props.text} />
                <SpinnerSvg />
                <RangeLabels type='spinner' />
                <RangeValues type={'spinner'} />
            </div>
        </RangeProvider>
    )
}
const SliderGroove: FC<{ grooveAttrs?: any }> = ({ grooveAttrs }) => {
    return <div {...UT.AddToAttrs(grooveAttrs, { className: 'ai-slider-groove' })}></div>
}
const SpinnerText: FC<{ text?: ReactNode }> = ({ text }) => {
    if (text === undefined || text === null) { return null }
    return <div className={'ai-range-text'}>{text}</div>
}
const RangeLabels: FC<{ type: 'slider' | 'spinner' }> = ({ type }) => {
    const { rangeHook } = useRangeProvider()
    const { labels } = rangeHook.rootProps;
    return (
        <>{labels.map((label: AI_label, i: number) => <RangeLabel key={i} label={label} type={type} />)}</>
    )
}
const RangeValues: FC<{ type: 'slider' | 'spinner' }> = ({ type }) => {
    const { rangeHook } = useRangeProvider();
    return (<>
        {
            rangeHook.values.map((itemValue, i) => {
                return <RangeValue index={i} itemValue={itemValue} key={'rangecontainervalue' + i} type={type} />
            })
        }
    </>)
}
const RangeValue: FC<{ itemValue: number, index: number, type: 'slider' | 'spinner' }> = ({ itemValue, index, type }) => {
    let { rangeHook } = useRangeProvider();
    const rangeValue = rangeHook.getRangeValue(itemValue, index)
    const props = type === 'spinner' ? rangeHook.getSpinnerValueProps(rangeValue) : rangeHook.getSliderValueProps(rangeValue)
    return (
        <div {...props}>
            <SpinnerHandle rangeValue={rangeValue} />
            <AIRangePoint rangeValue={rangeValue} type={type} />
        </div>
    )
}

const SpinnerSvg: FC = () => {
    const { rangeHook } = useRangeProvider();
    const { ranges, size } = rangeHook.rootProps;
    if (!ranges.length) { return null }
    return (<svg style={{ position: 'absolute', left: 0, top: 0 }} width={size} height={size}>
        {ranges.map((range, i) => <SpinnerArc key={'rangecirclearc' + i} range={range} />)}
    </svg>)
}
const SliderRects: FC<{ ranges?: I_range[] }> = ({ ranges = [] }) => {
    if (!ranges.length) { return null }
    return (<>{ranges.map((range, i) => <SliderRect range={range} key={'range' + i} />)}</>)
}
type I_RangeValue = { value: number, disabled: boolean, angle: number, index: number }
const SliderFills: FC = () => {
    const { rangeHook } = useRangeProvider();
    const { rootProps, values } = rangeHook;
    const { rangeOption = {}, start } = rootProps;
    const { fill } = rangeOption;
    let limit = values.length === 1 ? [start, values[0]] : [...values];
    const fillHtmls: ReactNode[] = [];
    for (let i = 1; i < limit.length; i++) {
        const startPoint = { index: i - 1, value: limit[i - 1] }
        const endPoint = { index: i, value: limit[i] }
        let res = !fill ? { thickness: 4 } : fill(startPoint, endPoint);
        if (res === false) { continue }
        res = res || { thickness: 4 }
        const { thickness = 4, style, className: fillClassName, color } = res;
        let from = limit[startPoint.index];
        let to = limit[endPoint.index];
        let className = 'ai-slider-fill';
        if (fillClassName) { className += ' ' + fillClassName }
        fillHtmls.push(<SliderRect range={{ thickness, color, from, to, className, style, offset: 0 }} key={'fill' + i} />)
    }
    return <>{fillHtmls}</>
}
const SliderRect: FC<{ range: I_range }> = ({ range }) => {
    let { rangeHook } = useRangeProvider();
    let { color, from, to, className, style, offset, thickness, roundCap } = range
    className = UT.classListToString(['ai-slider-range', range.className])
    const { vertical, reverse } = rangeHook.rootProps;
    let startSide = rangeHook.getXPByValue(from), endSide = rangeHook.getXPByValue(to);
    let Style: any = {
        [vertical ? 'height' : 'width']: (endSide - startSide) + '%',
        [vertical ? 'width' : 'height']: range.thickness,
        [vertical ? (reverse ? 'bottom' : 'top') : (reverse ? 'right' : 'left')]: startSide + '%',
        [vertical ? 'left' : 'top']: `calc(50% - ${(thickness / 2) + offset}px)`,
        borderRadius: roundCap ? 48 : 0,
        background: color,
        ...style
    }
    return <div className={className} style={Style} />
}
const SpinnerArc: FC<{ range: I_range }> = ({ range }) => {
    let { rangeHook } = useRangeProvider();
    let { size, reverse } = rangeHook.rootProps;
    const { thickness, color, radius, roundCap, full, from, to } = rangeHook.getArc(range)

    let a, b;
    let x = size / 2, y = size / 2;
    if (full) { a = 0; b = 360; }
    else {
        let startAngle = rangeHook.fixAngle(rangeHook.getAngleByValue(from) + 90);
        let endAngle = rangeHook.fixAngle(rangeHook.getAngleByValue(to) + 90);
        if (endAngle === 0) { endAngle = 360 }
        a = startAngle;
        b = endAngle;
        if (reverse) { b = startAngle; a = endAngle }
    }
    return <path key={`from${from}to${to}`} d={UT.svgArc(x, y, radius, a, b)} stroke={color} strokeWidth={thickness} fill='transparent' strokeLinecap={roundCap ? 'round' : undefined} className={range.className} />
}
const SpinnerHandle: FC<{ rangeValue: I_RangeValue }> = ({ rangeValue }) => {
    let { rangeHook } = useRangeProvider();
    let { rangeOption = {} } = rangeHook.rootProps;
    if (!rangeOption.handle) { return null }
    const { handle: handleFn } = rangeOption;
    const handle = handleFn(rangeValue)
    if (!handle) { return null }
    let { sharp = false, widthPercent = 10, heightPercent = 90, color = '#000', offset = 0 } = handle
    let width = rangeHook.sbp(heightPercent, true);
    let height = rangeHook.sbp(widthPercent, true) * 2
    function getStyle() {
        if (sharp) {
            return {
                [width < 0 ? 'borderRight' : 'borderLeft']: `${Math.abs(width)}px solid ${color}`,
                borderTop: `${height / 2}px solid transparent`,
                borderBottom: `${height / 2}px solid transparent`,
                left: rangeHook.sbp(offset)
            }
        }
        else { return { width, height, left: offset, background: color } }
    }
    let PROPS = UT.AddToAttrs({}, { className: 'ai-spinner-handle', style: getStyle(), attrs: { draggable: false } })
    return (<div {...PROPS} key={'rangehandle' + rangeValue.index}></div>)
}
type I_RangeLabel = { label: AI_label, type: 'slider' | 'spinner' }
const RangeLabel: FC<I_RangeLabel> = (props) => {
    let { rangeHook } = useRangeProvider()
    let { label } = props;
    let { zIndex, dynamic } = label;
    let { reverse, vertical } = rangeHook.rootProps;
    let [def] = useState<ReactNode>(getDef);
    function getDef() { return RENDER(true) }

    function updateLabels() {
        if (props.type !== "spinner" || !label.autoHide || vertical) { return }
        let container = $(rangeHook.mainDom.current);
        let labels = container.find(`.ai-range-label`);
        if (!labels.length) { return; }
        let firstLabel: any = labels.eq(0);
        let firstLabelHProp = firstLabel.attr('data-rotated') === 'yes' ? 'height' : 'width';
        let end = firstLabel.offset().left + (!reverse ? firstLabel[firstLabelHProp]() : 0);
        for (let i = 1; i < labels.length; i++) {
            let label: any = labels.eq(i);
            let hProp = label.attr('data-rotated') === 'yes' ? 'height' : 'width';
            label.css({ display: 'flex' })
            let left = label.offset().left
            let width = label[hProp]();
            let right = left + width;
            if (!reverse) {
                if (left < end + 5) { label.css({ display: 'none' }) }
                else { end = left + width; }
            }
            else {
                if (right > end - 5) { label.css({ display: 'none' }) }
                else { end = left; }
            }
        }
    }
    useEffect(() => { $(window).on('resize', updateLabels) }, [])
    useEffect(() => { updateLabels() })
    function RENDER(init: boolean): ReactNode {
        if (!init && !dynamic) { return def }
        const list = rangeHook.getLabelList(label);
        return (
            <div className={'ai-range-labels'} style={{ zIndex }}>
                {
                    list.map((itemValue, index) => {
                        let disabled = rangeHook.isValueDisabled(itemValue);
                        let labelItem: AI_labelItem = label.setting(itemValue, { disabled, angle: 0, index });
                        if (props.type === "slider") {return (rangeHook.getSliderLabel(labelItem,itemValue))}
                        else {return rangeHook.getSpinnerLabel(labelItem,itemValue)}
                    })
                }
            </div>
        )
    }
    return <>{RENDER(false)}</>
}
const AIRangePoint: FC<{ rangeValue: I_RangeValue, type: 'slider' | 'spinner' }> = ({ rangeValue, type }) => {
    let { rangeHook } = useRangeProvider();
    const { rangeOption = {} } = rangeHook.rootProps;
    const {
        pointContent = (() => rangeValue.value),
        pointHtml = (() => <div className={'ai-range-point'}>{pointContent(rangeValue)}</div>),
    } = rangeOption;
    const point = pointHtml(rangeValue)
    if (point === false) { return null }
    const containerStyle = type === "spinner" ? rangeHook.getSpinnerPointContainerStyle(rangeValue) : rangeHook.getSliderPointContainerStyle(rangeValue)
    return (
        <div
            key={'rangepoint' + rangeValue.index}
            style={containerStyle}
            className='ai-range-point-container'
            draggable={false}
            data-index={rangeValue.index}
            onMouseDown={() => rangeHook.handleZIndex(rangeValue.index)}
        >{point}</div>
    )
}

const useRange = (PROPS: I_Range,type:'slider' | 'spinner') => {
    const getProps = (): I_Range_default => {
        const { start = 0, end = 100, min = start, max = end, step = 1, size = 72, labels = [], rotate = 0, ranges = [] } = PROPS;
        return { ...PROPS, start, end, min, max, step, size, labels, rotate, ranges }
    }
    const props = getProps()
    const propsRef = useRef<I_Range>(props)
    propsRef.current = props
    let [temp] = useState<any>({ start: 0, index: false })
    const [mainDom] = useState<any>(createRef())
    const [values, setValues] = useState<number[]>([])
    const { vertical, reverse, start = 0, end = 100, min = start, max = end, step = 1, rotate = 0, round = 1, size = 72 } = props;
    useEffect(() => {
        if (!props.onChange || props.disabled === true) { return }
        clearTimeout(temp.timeOut)
        temp.timeOut = setTimeout(() => {
            new UT.Swip({
                reverseX: !!props.reverse,
                //vertical condition
                reverseY: !!props.reverse && !!props.vertical,
                dom: () => $(mainDom.current),
                start: (p: { event: Event }) => {
                    const { disabled } = propsRef.current;
                    if (disabled === true) { return false }
                    let { event } = p;
                    if (event.target !== null) {
                        let index = UT.GetAttr($(event.target), 'data-index')
                        temp.index = index === null ? false : +index
                        temp.start = [...values];
                    }
                    return [0, 0];
                },
                move: (p: UT.I_Swip_parameter) => {
                    let { change, mousePosition } = p;
                    if (change) { changeHandle({ dx: change.dx, dy: change.dy, deltaCenterAngle: change.deltaCenterAngle, centerAngle: mousePosition.centerAngle }) }
                },
                onClick: function (p: UT.I_Swip_parameter) {
                    const { disabled } = propsRef.current;
                    if (disabled) { return }
                    click(p.mousePosition)
                }
            })
        }, 100)
    }, [changeHandle])
    function click(mousePosition: UT.I_Swip_mousePosition) {
        if (props.disabled === true || temp.index !== false) { return }
        let clickedValue: number;
        //vertical condition
        if (type === "spinner") { clickedValue = getValueByAngle(mousePosition.centerAngle); }
        else { clickedValue = getValueByXP(mousePosition[props.vertical ? 'yp' : 'xp']); }
        if (clickedValue < values[values.length - 1] && clickedValue > values[0]) { return }
        if (clickedValue < values[0]) { change1Unit(-1) }
        else { change1Unit(1) }
    }
    function changeHandle(obj: { dx: number, dy: number, deltaCenterAngle: number, centerAngle: number }): void {
        if (props.disabled === true) { return }
        let newValue = getChangedValue(obj);
        changeValue(newValue)
    }
    function isValueDisabled(value: number): boolean {
        const { rangeOption = {} } = props;
        const { disabled: d = () => false } = rangeOption;
        return d(value);
    }
    function getChangedValue(obj: { dx: number, dy: number, deltaCenterAngle: number, centerAngle: number }): number[] {
        let { dx, dy, deltaCenterAngle, centerAngle } = obj;
        let startValue = [...temp.start];
        let index = temp.index;
        //agar faghat yek point darim har koja mousedown shod farz kon rooye oon point mousedown karde im
        if (startValue.length === 1 && index === false) { index = 0 }
        let res;
        if (index === false) {
            let deltaValue;
            if (type === "spinner") {
                let v = deltaCenterAngle * (props.end - props.start) / 360;
                v = Math.round(v / props.step) * props.step;
                deltaValue = v;
            }
            else { deltaValue = Math.round(getValueByXP(getXPByX(props.vertical ? dy : dx)) / props.step) * props.step; }
            let newValue = moveAll(startValue, deltaValue, true);
            res = !isValueValid(newValue) ? values : newValue;
        }

        else {
            let { before, after } = getIndexLimit(index)
            let newUnitValue: number;
            if (type === 'spinner') {
                newUnitValue = Math.round(getValueByAngle(centerAngle) / props.step) * props.step;
            }
            else {
                let deltaValue = Math.round(getValueByXP(getXPByX(props.vertical ? dy : dx)) / props.step) * props.step;
                newUnitValue = startValue[index] + deltaValue;
            }
            if (newUnitValue > after) { newUnitValue = after }
            if (newUnitValue < before) { newUnitValue = before }
            if (isValueDisabled(newUnitValue)) {
                newUnitValue = values[index]
            }
            startValue[index] = newUnitValue;
            res = startValue;
        }
        return res
    }
    function change1Unit(dir: 1 | -1): void {
        let newValue = [...values];
        let lastValue = JSON.stringify(newValue)
        newValue = moveAll(newValue, dir * props.step);
        while (!isValueValid(newValue) && JSON.stringify(newValue) !== lastValue) {
            lastValue = JSON.stringify(newValue)
            newValue = moveAll(newValue, dir * props.step)
        }
        changeValue(newValue)
    }
    function changeValue(newValue: number[]) {
        if (!props.onChange || props.disabled === true) { return }
        newValue = getValues(newValue)
        props.onChange(!!props.multiple ? newValue : newValue[0])
    }
    function isValueValid(value: number[]): boolean {
        for (let i = 0; i < value.length; i++) { if (isValueDisabled(value[i])) { return false } }
        return true
    }

    function getIndexLimit(index: number) {
        let before: number, after: number;
        if (index === 0) { before = props.start }
        else { before = values[index - 1] }
        if (index === values.length - 1) { after = props.end }
        else { after = values[index + 1] }
        return { before, after }
    }
    function moveAll(newValue: number[], offset: number, ifFailedReturnOriginalValue?: boolean): number[] {
        let res = newValue.map((o: number) => o + offset)
        if (res[0] < props.start || res[res.length - 1] > props.end) {
            return ifFailedReturnOriginalValue ? values : newValue
        }
        return res;
    }
    function getValues(value: any): number[] {
        if (!Array.isArray(value)) { value = [value || 0] }
        for (let i = 0; i < value.length; i++) {
            let point = value[i] || 0;
            point = Math.round((point - start) / step) * step + start;
            const { floatLength } = UT.Float(step)
            point = +point.toFixed(floatLength)
            if (point < min) { point = min; }
            if (point > max) { point = max; }
            value[i] = point;
        }
        return value
    }
    useEffect(() => {
        setValues(getValues(props.value))
    }, [props.value])


    function getSide() {
        return vertical ? (reverse ? 'bottom' : 'top') : (reverse ? 'right' : 'left')
    }

    function fixValueInEmpty(value: number) {
        let fill = round;
        let empty = 1 - fill;
        let emptyValue = empty * (end - start) / fill;
        if (value > end + emptyValue / 2) {
            value = start - emptyValue + value - end
        }
        return value;
    }
    function fixAngle(angle: number): number { angle = angle % 360; return angle < 0 ? angle = 360 + angle : angle }
    function getValueByAngle(angle: number) {
        let fillAngle = 360 * round;
        let emptyAngle = 360 - fillAngle;
        if (reverse) { angle = 180 - angle }
        angle -= rotate;
        angle -= emptyAngle / 2;
        angle -= 90;
        angle = fixAngle(angle);
        let res = angle * (end - start) / fillAngle;
        res = fixValueInEmpty(res);
        return res
    }
    function getAngleByValue(value: number, ang?: number) {
        let fillAngle = 360 * round;
        let emptyAngle = 360 - fillAngle;
        let res = value * fillAngle / (end - start);
        res += 90; res += emptyAngle / 2;
        res += rotate;
        res += (ang || 0)
        return reverse ? res = 180 - res : res;
    }
    const sbp: AI_sbp = (value, half) => size / (half ? 2 : 1) * value / 100
    const getRectByStr: AI_rbs = (obj) => {
        let { thickness = 1, offset = 0, color = '#000', roundCap = false, className, from = start, to = end } = obj;
        try {
            let thicknessValue = thickness;
            if (isNaN(thicknessValue)) { thicknessValue = 1 }
            thickness = thicknessValue;
            let offsetValue = offset;
            if (isNaN(offsetValue)) { offsetValue = 0 }
            let defaultOffset = (size / 2) - (thickness / 2);
            offset = defaultOffset - offsetValue
            if (offset > size - thickness / 2) { offset = size - thickness / 2 }
            if (offset < thickness / 2) { offset = thickness / 2 }
        }
        catch { }
        return { thickness, offset, color, roundCap, className, from, to }
    }
    function getXPByValue(value: number): number {
        return 100 * (value - start) / (end - start);
    }
    function getValueByXP(xp: number): number {
        return xp * (end - start) / 100;
    }
    function getXPByX(x: number): number {
        return x * 100 / ($(mainDom.current)[vertical ? 'height' : 'width']() as number);
    }

    const getArc = (rc: I_range): { thickness: number, color: string, roundCap: boolean, full: boolean, radius: number, className?: string, from: number, to: number } => {
        let roundCap = rc.roundCap || false, full = rc.full || false, offset = rc.offset, color = rc.color || '#000', className = rc.className;
        const { from = start, to = end } = rc
        const thickness = size * rc.thickness / 100;
        const radius = (rc.offset * size / 2 / 100) - thickness / 2;
        offset = sbp(offset)
        return { thickness, radius, color, roundCap, full, className, from, to }
    }

    function handleZIndex(index: number) {
        let containers = $(mainDom.current).find(`.ai-range-value-container`);
        containers.css({ zIndex: 10 });
        containers.eq(index).css({ zIndex: 100 })
    }
    function getOffset(rangeValue: I_RangeValue) {
        const { pointOffset = (() => 0) } = props.rangeOption || {};
        return pointOffset(rangeValue) || 0
    }
    function getSliderPointContainerStyle(rangeValue: I_RangeValue) { return { [vertical ? 'left' : 'top']: getOffset(rangeValue) } }
    function getSpinnerPointContainerStyle(rangeValue: I_RangeValue) { return { left: size / 2 + getOffset(rangeValue), transform: `rotate(${-rangeValue.angle}deg)` } }
    function getSliderValueProps(rangeValue: I_RangeValue) { return { className: 'ai-range-value-container', draggable: false, style: { [getSide()]: getXPByValue(rangeValue.value) + '%' } } }
    function getSpinnerValueProps(rangeValue: I_RangeValue) {
        return { className: 'ai-range-value-container', draggable: false, style: { transform: `rotate(${rangeValue.angle}deg)` } }
    }
    const getRangeValue = (itemValue: number, index: number) => {
        let angle = fixAngle(getAngleByValue(itemValue));
        const disabled = isValueDisabled(itemValue);
        return { disabled, value: itemValue, index, angle }

    }
    function getRootClassName(props: I_Range, type: 'slider' | 'spinner') {
        let c = 'ai-range';
        if (type === "spinner") { c += ` ai-spinner` }
        else { c += props.vertical ? ` ai-slider-vertical` : ` ai-slider-horizontal` }
        if (props.className) { c += ' ' + props.className }
        if (props.reverse) { c += ` ai-${type === "spinner" ? 'spinner' : 'slider'}-reverse` }
        return c
    }
    function getRootStyle(props: I_Range, type: 'slider' | 'spinner') {
        let { style } = props;
        let res: any;
        if (type === "spinner") { res = { width: props.size, height: props.size } }
        return { ...res, ...style, }
    }
    function getRootProps(props: I_Range, type: 'slider' | 'spinner') {
        let { attrs = {} } = props;
        let rootStyle = getRootStyle(props, type);
        return UT.AddToAttrs(attrs, { className: getRootClassName(props, type), style: rootStyle, attrs: { ref: mainDom } })
    }
    function getDistance(labelItem: AI_labelItem, type: 'slider' | 'spinner') {
        let { offset = 0 } = labelItem;
        let distance: any = { [type === "spinner" || vertical ? 'left' : 'top']: size * offset / 100 / 2 }
        if (type === "spinner") { distance.left = size * offset / 100 / 2 }
        else if (vertical) {
            distance.left = `calc(50% + ${offset || 0}px)`
        }
        else {
            distance.top = `calc(50% + ${offset || 0}px)`
        }
        return distance
    }
    function getSliderLabel(labelItem:AI_labelItem,itemValue:number){
        let { html = '', config } = labelItem;
        if (config) {
            html = (
                <div style={{
                    [!vertical ? 'width' : 'height']: size * config.thickness / 100,
                    [!vertical ? 'height' : 'width']: size * config.height / 100,
                    background: config.color
                }}></div>
            )
        }
        const htmlProps = UT.AddToAttrs({}, { className: ['ai-range-label'], attrs: { draggable: false } })
        const containerStyle = { [getSide()]: getXPByValue(itemValue) + '%', ...getDistance(labelItem, 'slider') };
        const containerProps = { className: 'ai-range-label-container', style: containerStyle, draggable: false };
        return (
            <div {...containerProps}>
                <div {...htmlProps}>{html}</div>
            </div>
        )
    }

    function getLabelList(label: AI_label): number[] {
        let { step, list = [] } = label;
        let res: number[] = [];
        if (step) {
            let { start: lstart = start, end: lend = end } = label;
            for (let i = lstart; i <= lend; i += step) { res.push(i) }
        }
        for (let i = 0; i < list.length; i++) {
            if (res.indexOf(list[i]) === -1) { res.push(list[i]) }
        }
        return res;
    }
    function getSpinnerLabel(labelItem:AI_labelItem,itemValue:number) {
        let { html = '', config } = labelItem;
        if (config) {
            html = (
                <div style={{
                    height: size * config.thickness / 100,
                    width: size * config.height / 100,
                    background: config.color
                }}></div>
            )
        }
        let angle: number = fixAngle(getAngleByValue(itemValue)) || 0
        let containerStyle = { transform: `rotate(${angle}deg)` }
        let containerProps = { className: 'ai-range-label-container', style: containerStyle, draggable: false };
        const distance = getDistance(labelItem, "spinner")
        let textStyle: any = { ...distance,transform: labelItem.fixAngle?`rotate(${-angle}deg)`:undefined }
        const htmlProps = UT.AddToAttrs({}, { className: ['ai-range-label'], style: textStyle, attrs: { draggable: false } })
        return (
            <div {...containerProps}>
                <div {...htmlProps}>{html}</div>
            </div>
        )
    }
    return {
        values, getValues, getSide, fixAngle, getValueByAngle, mainDom, isValueDisabled, rootProps: props, getRootProps, getLabelList,
        getArc, getAngleByValue, getRectByStr, sbp, getXPByValue, getValueByXP, getXPByX, handleZIndex, getSpinnerLabel,
        getSliderPointContainerStyle, getSpinnerPointContainerStyle, getSliderValueProps, getSpinnerValueProps, getRangeValue,
        getSliderLabel
    }

}